<script setup lang="ts">
import { DecorInserts } from "@innoclouds/ui";
import { useTranslation } from "i18next-vue";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import ArrowLeft from "#/assets/iconsDynamic/ArrowLeft.vue";
import Heart from "#/assets/iconsDynamic/Heart.vue";
import Info from "#/assets/iconsDynamic/Info.vue";
import PopupCombo from "#/components/popupCombo/PopupCombo.vue";
import ProductAllergens from "#/components/productAllergens/ProductAllergens.vue";
import ProductBadges from "#/components/productBadges/ProductBadges.vue";
import SystemNotification from "#/components/SystemNotification/SystemNotification.vue";
import { useProductInsertFields } from "#/composables/useProductInsertFields";
import { useProductLike } from "#/composables/useProductLike";
import { useProductNutritions } from "#/composables/useProductNutritions";
import { vMuted } from "#/directives/vMuted";
import ButtonPriceCount from "#/semantics/cardProduct/buttonPriceCount/ButtonPriceCount.vue";
import CustomSwiperModifier from "#/semantics/cardProduct/customSwiperModifier/CustomSwiperModifier.vue";
import LiquidModifiers from "#/semantics/cardProduct/liquidModifiers/LiquidModifiers.vue";
import ModalAmount from "#/semantics/cardProduct/modalAmount/ModalAmount.vue";
import PlusModifier from "#/semantics/cardProduct/plusModifier/PlusModifier.vue";
import SectionModifiers from "#/semantics/cardProduct/sectionModifiers/SectionModifiers.vue";
import SizeModifier from "#/semantics/cardProduct/sizeModifier/SizeModifier.vue";
import { useBadgesStore } from "#/store/badges";
import { useCardStore } from "#/store/card";
import { useDecorationStore } from "#/store/decoration";
import { useLikesStore } from "#/store/likes";
import { useThemeStore } from "#/store/theme";
import { BottomSheet } from "#/ui/bottomSheet";
import NutritionBasis from "#/ui/nutritionBasis/NutritionBasis.vue";
import { hasRegularModifierGroups as hasRegularGroups } from "#/utils/customRenderModifiers";
import { formatPrice, getLocaleFor, getLocaleName } from "#/utils/i18next";

const themeStore = useThemeStore();
const cardStore = useCardStore();
const badgesStore = useBadgesStore();
const likesStore = useLikesStore();
const decor = useDecorationStore().screen("product_card");

const { stateCard, isOpenWindow, isCombo, isAddToCard } = storeToRefs(cardStore);
const router = useRouter();
const route = useRoute();
const { t, i18next } = useTranslation();

const props = defineProps<{
	configPage: {
		closeProduct: any;
		is_closeProduct: boolean;
		data: any;
		qr_menu: boolean;
		is_kiosk: boolean;
	};
}>();

const showCombo = computed(() => props.configPage.is_kiosk && isCombo.value);

const pdfMode = computed(() => {
	if (route.query.mode === "pdf" && props.configPage.qr_menu) return true;
	return false;
});

const product = computed(() => stateCard.value.openCard);
const productId = computed(() => product.value?.id ?? null);
const { isLikesEnabled, isLiked, isLikePending, likeCount, toggleLike } = useProductLike(productId);
const showLike = computed(() => isLikesEnabled.value && !!productId.value);
const title = computed(() => getLocaleName(product.value, i18next.language));
const description = computed(() => getLocaleFor("description", product.value, i18next.language));
const hasBadges = computed(() => themeStore.data?.show_badges !== false && badgesStore.resolve(product.value?.badges).length > 0);
const { nutritions, hasBasisToggle, hasMacroNutritions, hasWeight, weightLabel } = useProductNutritions();
const insertFields = useProductInsertFields();

const hasRegularModifierGroups = computed(() => hasRegularGroups(stateCard.value.groupModifiers));
const isLiquid = computed(() => props.configPage.qr_menu && themeStore.data?.qr_modifiers_design === "new");
const isDescriptionOpen = ref(false);

const kbjuItems = computed(() => {
	if (!hasMacroNutritions.value) return [];
	const symbol = t(`cart.header.nutritional.symbol`);
	const { energy_amount, proteins_amount, fat_amount, carbohydrates_amount } = nutritions.value;
	return [
		{ key: "energy", value: t(`modal.product-description.energy-amount`, { amount: formatPrice(energy_amount) }), label: t(`card.nutritional.energy`) },
		{ key: "proteins", value: formatPrice(proteins_amount, symbol), label: t(`card.nutritional.proteins`) },
		{ key: "fats", value: formatPrice(fat_amount, symbol), label: t(`card.nutritional.fats`) },
		{ key: "carbohydrates", value: formatPrice(carbohydrates_amount, symbol), label: t(`card.nutritional.carbohydrates`) },
	];
});
const hasDetails = computed(() => !!description.value || hasBadges.value || kbjuItems.value.length > 0);

const redirectToBack = () => {
	if (props.configPage.qr_menu) {
		if (pdfMode.value) {
			router.push(`/${route.params.orgId}?mode=pdf`);
		} else {
			router.push(`/${route.params.orgId}`);
		}
	} else {
		props.configPage.closeProduct();
	}
};

const closeSheet = () => {
	isAddToCard.value = false;
	stateCard.value.isSectionModifier = false;
};

const closeModalAmount = () => {
	stateCard.value.isModifiedAmount = false;
};

const buttonPriceRef = ref<InstanceType<typeof ButtonPriceCount> | null>(null);
const handleAddToCart = () => {
	buttonPriceRef.value?.addProductToCart();
};

watch(() => props.configPage.data, cardStore.setCurrentCard);
watch(
	product,
	(value) => {
		if (value?.id) likesStore.setCount(value.id, value.likes_count ?? 0);
	},
	{ immediate: true },
);

watch(
	[() => stateCard.value.isSectionModifier, isLiquid],
	([isSectionModifier, liquid]) => {
		if (pdfMode.value) document.body.classList.toggle("pdf_hidden", isSectionModifier && !liquid);
	},
	{ immediate: true },
);

watch([() => product.value?.id, hasDetails], () => {
	isDescriptionOpen.value = false;
});

watch(
	() => product.value?.badges,
	(badges) => {
		if (badges?.length) void badgesStore.ensureLoaded();
	},
	{ immediate: true },
);

watchEffect(() => {
	if (!isOpenWindow.value) isOpenWindow.value = props.configPage.is_closeProduct;
});

onBeforeUnmount(() => cardStore.reset());
</script>

<template>
	<div
		class="card_product_wrapper"
		:class="{ card_product_wrapper_liquid: isLiquid, card_product_wrapper_kiosk: configPage.is_kiosk }"
		:style="pdfMode ? 'user-select: none;' : ''"
		v-bind="decor.bind('card')"
	>
		<BottomSheet v-if="!isLiquid" v-model="stateCard.isSectionModifier" class="card_product_modifiers_sheet" :teleportTo="pdfMode ? '#app' : 'body'">
			<div class="card_product_modifiers_sheet_body">
				<SectionModifiers @close="closeSheet" @add-to-cart="handleAddToCart" />
				<PlusModifier :isSectionModifier="stateCard.isSectionModifier" :toggleNotRequire="cardStore.toggleNotRequire" />
			</div>
		</BottomSheet>
		<Teleport to="body">
			<div v-if="stateCard.isModifiedAmount" class="wrapper_modified_amount" @click.stop="closeModalAmount">
				<ModalAmount />
			</div>
		</Teleport>

		<template v-if="isLiquid">
			<video v-if="product?.video_link" loop autoplay muted v-muted playsinline class="card_liquid_media" crossorigin="anonymous">
				<source :src="product?.video_link" type="video/webm">
			</video>
			<img v-else class="card_liquid_media" :src="product?.image_link || themeStore.data?.fallback_image || ''" crossorigin="anonymous" alt="">
			<div class="card_liquid_layout">
				<SystemNotification />
				<div class="card_liquid_header">
					<div class="card_liquid_header_row">
						<ArrowLeft class="card_header_back" @click="redirectToBack" />
						<button v-if="hasDetails" type="button" class="card_liquid_info" @click="isDescriptionOpen = true">
							<Info />
						</button>
						<button
							v-if="showLike"
							type="button"
							class="card_liquid_like"
							:data-liked="isLiked"
							:data-pending="isLikePending"
							:disabled="isLikePending"
							@click.stop="toggleLike"
						>
							<span v-if="likeCount > 0">{{ likeCount }}</span>
							<Heart :filled="isLiked" />
						</button>
					</div>
				</div>
				<div class="card_liquid_title_wrapper">
					<div class="card_liquid_title">
						<span class="card_liquid_title_text">{{ title }}</span>
						<span v-if="hasWeight" class="card_liquid_title_weight">{{ weightLabel }}</span>
					</div>
				</div>
				<div class="card_liquid_options">
					<SizeModifier />
					<CustomSwiperModifier class="card_liquid_swiper" />
				</div>
				<LiquidModifiers v-if="hasRegularModifierGroups" inline class="card_liquid_modifiers" />
				<ProductAllergens class="card_liquid_allergens" />
			</div>
			<ButtonPriceCount ref="buttonPriceRef" :qr_menu="configPage.qr_menu" :closeProduct="configPage.closeProduct" />
			<BottomSheet v-if="hasDetails" v-model="isDescriptionOpen" class="card_liquid_description_sheet" :teleportTo="pdfMode ? '#app' : 'body'">
				<div class="card_liquid_description_body">
					<p class="card_liquid_description_title">{{ title }}</p>
					<div v-if="kbjuItems.length" class="card_liquid_kbju">
						<NutritionBasis v-if="hasBasisToggle" decoration="product_card" />
						<div v-for="item in kbjuItems" :key="item.key" class="card_liquid_kbju_item">
							<span class="card_liquid_kbju_value">{{ item.value }}</span>
							<span class="card_liquid_kbju_label">{{ item.label }}</span>
						</div>
					</div>
					<ProductBadges :badges="product?.badges" placement="inline" class="card_liquid_description_badges" />
					<p v-if="description" class="card_liquid_description">{{ description }}</p>
				</div>
			</BottomSheet>
		</template>
		<div v-else class="card__pictures" v-bind="decor.bind('hero')">
			<video v-if="product?.video_link" loop autoplay muted v-muted playsinline class="card__video" crossorigin="anonymous" v-bind="decor.bind('photo')">
				<source :src="product?.video_link" type="video/webm">
			</video>
			<img
				v-else
				class="card__pictures_images"
				:src="product?.image_link || themeStore.data?.fallback_image || ''"
				crossorigin="anonymous"
				alt=""
				v-bind="decor.bind('photo')"
			>
			<div class="card_header_wrapper_container">
				<SystemNotification />
				<div class="card_header_wrapper" v-bind="decor.bind('header')">
					<ArrowLeft class="card_header_back" v-bind="decor.bind('back_button')" @click="redirectToBack" />
					<button
						v-if="showLike"
						type="button"
						class="card_product_like"
						:data-liked="isLiked"
						:data-pending="isLikePending"
						:disabled="isLikePending"
						v-bind="decor.bind('like_button')"
						@click.stop="toggleLike"
					>
						<span v-if="likeCount > 0">{{ likeCount }}</span>
						<Heart :filled="isLiked" v-bind="decor.bind('like_icon')" />
					</button>
					<div v-if="hasMacroNutritions" class="card_nutritions_wrapper" v-bind="decor.bind('nutritions')">
						<NutritionBasis v-if="hasBasisToggle" decoration="product_card" />
						<div class="card_nutritions_item" v-bind="decor.bind('nutritions_item')">
							<strong>{{ formatPrice(nutritions.energy_amount) }}</strong>
							<span>{{ t(`modal.product-description.energy-amount`, { amount: '' }) }}</span>
						</div>
						<div class="card_nutritions_item" v-bind="decor.bind('nutritions_item')">
							<strong>{{ formatPrice(nutritions.proteins_amount) }}</strong>
							<span>{{ t(`modal.product-description.proteins-amount`, { amount: '' }) }}</span>
						</div>
						<div class="card_nutritions_item" v-bind="decor.bind('nutritions_item')">
							<strong>{{ formatPrice(nutritions.fat_amount) }}</strong>
							<span>{{ t(`modal.product-description.fat-amount`, { amount: '' }) }}</span>
						</div>
						<div class="card_nutritions_item" v-bind="decor.bind('nutritions_item')">
							<strong>{{ formatPrice(nutritions.carbohydrates_amount) }}</strong>
							<span>{{ t(`modal.product-description.carbohydrates-amount`, { amount: '' }) }}</span>
						</div>
					</div>
				</div>
			</div>
			<div class="card_product_title_wrapper" v-bind="decor.bind('title')">
				<h4 v-bind="decor.bind('title_text')">{{ title }}</h4>
				<DecorInserts :nodes="decor.inserts('title')" :decor="decor" :fields="insertFields" />
				<p v-if="hasWeight" class="card_product_weight" v-bind="decor.bind('weight')">
					{{ weightLabel }}
				</p>
			</div>
			<div
				v-if="decor.visible('photo_scrim')"
				class="card__pictures__gradient"
				:style="themeStore.data?.gradientStyle"
				v-bind="decor.bind('photo_scrim')"
			></div>
		</div>
		<div v-if="!isLiquid" class="card_content" v-bind="decor.bind('content')">
			<ProductBadges :badges="product?.badges" placement="inline" class="card_product_badges" v-bind="decor.bind('badges')" />
			<div class="wrapper_size" v-bind="decor.bind('options')">
				<SizeModifier v-bind="decor.bind('sizes')" />
				<CustomSwiperModifier class="card_product_swiper" v-bind="decor.bind('swiper')" />
			</div>
			<ProductAllergens class="card_product_allergens" />
			<p v-if="description" class="description" v-bind="decor.bind('description')">{{ description }}</p>
			<div v-if="hasRegularModifierGroups" class="wrapper_modifiers" v-bind="decor.bind('modifiers')">
				<p class="title_info_modifiers" v-bind="decor.bind('modifiers_title')">
					{{ themeStore.data?.dish_cart_mod_choice_text || t(`product-card.modifiers.title`) }}
				</p>
				<PlusModifier :isSectionModifier="stateCard.isSectionModifier" :toggleNotRequire="cardStore.toggleNotRequire" />
			</div>
			<ButtonPriceCount ref="buttonPriceRef" :qr_menu="configPage.qr_menu" :closeProduct="configPage.closeProduct" />
		</div>
		<PopupCombo v-if="showCombo" :visible="isCombo" :qr_menu="configPage.qr_menu" @close-product="redirectToBack" />
	</div>
</template>

<style scoped lang="scss">
  @use './cardProductPage.scss';
</style>
